import React, { Component } from "react";
import { Spin, notification, Divider, Modal, Input } from "antd";
import { withRouter } from "react-router";
import { list, noAuthadd, noAuthgetDetails } from "../../api/Apis";
import { withAppContext } from "../../store/contextStore";
import creditIcon from "../../images/plans/credit/credit.png";

// ── Brand tokens ──────────────────────────────────────────────────────────────
const NAVY   = "#1A2280";
const GOLD   = "#D4921A";
const BORDER = "#E2E8F0";
const MUTED  = "#718096";
const WHITE  = "#FFFFFF";
const LIGHT  = "#F7F9FC";
const GREEN  = "#276749";
const CREAM  = "#F5F0E8";
const DARK   = "#1A1A1A";

// ── Stripe payment links ──────────────────────────────────────────────────────
const STRIPE_LINKS = {
  // Agent / Lender plans
  agent_basic:        "https://buy.stripe.com/fZu8wOdxYgpX2Bv9Nu8og1K",
  agent_advanced:     "https://buy.stripe.com/9B6fZg3Xo0qZ5NHbVC8og1L",
  agent_professional: "https://buy.stripe.com/7sY00ifG60qZekd6Bi8og1M",
  agent_team_leader:  "https://buy.stripe.com/7sY3cudxYa1za3X3p68og1N",
  // Vendor plans
  vendor_basic:        "https://buy.stripe.com/bJefZgctU8Xv4JDbVC8og1O",
  vendor_advanced:     "https://buy.stripe.com/eVq6oGdxYa1zekd4ta8og1P",
  vendor_growth:       "https://buy.stripe.com/14AbJ0dxY1v33Fz0cU8og1Q",
};

const CALL_URL = "https://go.myhomebuddha.com/agents/lenders";
const LEARN_URL = "https://go.myhomebuddha.com/agents/lenders";

const FOUNDING_SPOTS_LEFT  = 433;
const FOUNDING_SPOTS_TOTAL = 500;

// ── Tier detection from DB record ─────────────────────────────────────────────
const getTierFromPrice = (price, planTier) => {
  // planTier field is the reliable source — always prefer it
  if (planTier && planTier !== "undefined" && planTier !== "null") return planTier;
  const p = parseInt(price) || 0;
  if (p >= 199) return "team_leader";
  if (p >= 60)  return "professional";
  if (p >= 15)  return "advanced";   // $19 founding price
  if (p >= 1)   return "basic";      // $9-$14 = basic
  return "free";
};

// ─────────────────────────────────────────────────────────────────────────────
// AGENT / LENDER CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const AGENT_TIERS = ["free","basic","advanced","professional"];

const AGENT_TIER_CONFIG = {
  free:         { emoji: "🌱", color: MUTED,    name: "Free",         tagline: "Get started",          isPopular: false, isFounding: false, founding: 0,   futurePrice: 0,   stripeKey: null },
  basic:        { emoji: "⭐", color: "#2B6CB0", name: "Basic",        tagline: "Build your presence",  isPopular: false, isFounding: false, founding: 9,   futurePrice: 25,  stripeKey: "agent_basic" },
  advanced:     { emoji: "🚀", color: NAVY,      name: "Advanced",     tagline: "Grow your business",   isPopular: true,  isFounding: true,  founding: 19,  futurePrice: 49,  stripeKey: "agent_advanced" },
  professional: { emoji: "👑", color: "#744210", name: "Professional", tagline: "Dominate your market", isPopular: false, isFounding: true,  founding: 69,  futurePrice: 99,  stripeKey: "agent_professional" },
  team_leader:  { emoji: "🏆", color: GREEN,     name: "Team Leader",  tagline: "Lead your market",     isPopular: false, isFounding: true,  founding: 199, futurePrice: 299, stripeKey: "agent_team_leader" },
};

const AGENT_COMPARISON_ROWS = [
  { section: "COVERAGE & VISIBILITY" },
  { label: "Zip codes covered",           tip: "Number of zip codes where you appear in searches",      free: "1",  basic: "{z}", advanced: "{z}", professional: "{z}" },
  { label: "Specialty tags",              tip: "Categories like New Construction, Investors, Downsizing", free: "1", basic: "{s}", advanced: "{s}", professional: "{s}" },
  { label: "Agent search page listing",   tip: "Appear on the Find an Agent search page",               free: false, basic: true,  advanced: true,  professional: true  },
  { label: "Premier status",              tip: "Premier badge on your profile",                         free: false, basic: true,  advanced: true,  professional: true  },
  { label: '"Speak to buyers" popup',     tip: "Your name appears when buyers request an agent",        free: false, basic: true,  advanced: true,  professional: true  },
  { label: "Top-of-town placement",       tip: "Profile pinned at top of town/zip searches",            free: false, basic: false, advanced: false, professional: true  },
  { section: "AI TOOLS & REPORTS" },
  { label: "Run AI home reports",         tip: "Full buyer, seller, and homeowner AI tools",            free: "View only", basic: true, advanced: true, professional: true },
  { label: "Download & share reports",    tip: "PDF download and client share links",                   free: false, basic: true,  advanced: true,  professional: true  },
  { label: "Branded AI reports",          tip: "Your name and logo on every report",                    free: false, basic: false, advanced: true,  professional: true  },
  { label: "Home Portals for clients",    tip: "Home Buddha home health portals for clients",           free: "1",   basic: "3",   advanced: "Unlimited", professional: "Unlimited" },
  { label: '"Build your own" report',     tip: "Custom home facts reports under your brand",            free: false, basic: false, advanced: true,  professional: true  },
  { label: "Buyer research tool",         tip: "Neighborhood, school, and market research",             free: false, basic: false, advanced: true,  professional: true  },
  { section: "LEAD GENERATION" },
  { label: "Unclaimed buyer referrals",   tip: "Get matched with buyers in your zip codes",             free: false, basic: false, advanced: true,  professional: true  },
  { label: "Home Buddha lead magnet",     tip: "Your branded AI tool link to share on social/email",   free: false, basic: false, advanced: true,  professional: true  },
  { label: "Content studio & social",     tip: "AI-generated social posts and market updates",         free: false, basic: false, advanced: true,  professional: true  },
  { section: "REVENUE & EQUITY" },
  { label: "Revenue & equity program",    tip: "Earn equity participation as MHB grows",               free: false, basic: false, advanced: false, professional: true  },
  { label: '"Founding Partner" badge',    tip: "Permanent badge on your public profile",               free: false, basic: false, advanced: true,  professional: true  },
  { label: "Priority support",            tip: "Dedicated support response within 4 hours",            free: false, basic: false, advanced: false, professional: true  },
];

// ─────────────────────────────────────────────────────────────────────────────
// VENDOR CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const VENDOR_TIERS = ["vendor_free","vendor_basic","vendor_advanced","vendor_growth"];

const VENDOR_TIER_CONFIG = {
  vendor_free: {
    emoji: "🌱", color: "#744210", name: "Free", tagline: "GET STARTED",
    price: 0, isPopular: false, isFounding: false, isApply: false, isDark: false,
    stripeKey: null,
    features: [
      "Listed on the Home Buddha Marketplace",
      "Visible to local homeowners browsing the platform",
      "Part of the Home Buddha trusted ecosystem",
      { text: "Not included in direct recommendation referrals", muted: true },
    ],
    cta: "Create free account",
  },
  vendor_basic: {
    emoji: "⭐", color: "#744210", name: "Basic", tagline: "STAND OUT",
    price: null, isPopular: false, isFounding: true, isApply: false, isDark: false,
    stripeKey: "vendor_basic",
    features: [
      "Enhanced Marketplace visibility",
      "Premier Vendor status badge",
      "Coverage across up to {z} zip codes",
      "Second priority in recommendation referrals",
      "Reach more homeowners across a wider area",
    ],
    cta: "Get started — ${p}/mo",
    futurePrice: 50,
  },
  vendor_advanced: {
    emoji: "🏆", color: "#744210", name: "Advanced", tagline: "DOMINATE YOUR MARKET",
    price: null, isPopular: true, isFounding: true, isApply: false, isDark: false,
    stripeKey: "vendor_advanced",
    features: [
      "Top-of-page placement in your zip codes",
      "Premier Vendor status across up to {z} zip codes",
      "First priority in all direct recommendation referrals",
      "Featured directly inside Home Checkup Reports",
      "Maximum brand authority and trust in your local market",
    ],
    cta: "Get Advanced — ${p}/mo",
    futurePrice: 100,
  },
  vendor_growth: {
    emoji: "🚀", color: GOLD, name: "Growth Program", tagline: "FULL-SCALE GROWTH",
    price: 250, isPopular: false, isFounding: false, isApply: false, isDark: true,
    stripeKey: "vendor_growth",
    features: [
      "Everything in the Advanced plan",
      "Dedicated lead management program built for you",
      "Lead ad creation & management included",
      "First-in-line for all recommendation referrals",
      "Weekly growth coaching calls",
      "Every buyer or seller you refer earns a 25% referral reward back into your lead program",
      "Certified Home Health Checkups at $250/home ($50 discount)",
    ],
    cta: "Apply for Growth Program",
  },
};

const getVendorTierFromPrice = (price, planTier) => {
  if (planTier === "vendor_growth")   return "vendor_growth";
  if (planTier === "vendor_advanced") return "vendor_advanced";
  if (planTier === "vendor_basic")    return "vendor_basic";
  if (planTier === "vendor_free")     return "vendor_free";
  const p = parseInt(price) || 0;
  if (p >= 250) return "vendor_growth";
  if (p >= 80)  return "vendor_advanced";
  if (p >= 30)  return "vendor_basic";
  return "vendor_free";
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export class Subscriptions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true, plansList: [], userPlan: null, userProfile: null,
      paymentVisible: false, selectedPlan: null,
      cardholderName: "", cardNumber: "", cardMonth: "", cardYear: "", cardCode: "",
      submitting: false,
      showUpgradeBanner: false,
      upgradeBannerPlan: null,
      _bannerKey: "",
    };
  }

  componentDidMount() { this.loadData(); }

  loadData = async () => {
    try {
      const { userDetails } = this.props.context.state;
      const userProfile = userDetails?.userProfile || "AG";

      // Fetch fresh user record from DB — don't trust cached userDetails.subscriptionPack
      // Admin may have changed the plan since login
      let freshUserPlan = userDetails?.subscriptionPack || null;
      if (userDetails?.id) {
        try {
          const freshUser = await noAuthgetDetails("getAgents", userDetails.id);
          if (freshUser?.success && freshUser?.data?.subscriptionPack) {
            freshUserPlan = freshUser.data.subscriptionPack;
          }
        } catch(e) {
          console.log("[Subscriptions] fresh user fetch failed, using cached:", e.message);
        }
      }

      const resp = await list("subscriptionPacks");
      if (resp.success) {
        const all = resp.data.sort((a, b) => parseInt(a.price || 0) - parseInt(b.price || 0));
        console.log('[Subscriptions] DB plans loaded:', all.map(p => ({
          name: p.planTitle || p.name, price: p.price, planTier: p.planTier, userType: p.userType,
        })));
        // Find the full pack object for the user's current plan
        let userPlanObj = null;
        if (freshUserPlan) {
          if (typeof freshUserPlan === "object") {
            userPlanObj = freshUserPlan;
          } else {
            // It's a UUID — find the matching pack
            userPlanObj = all.find(p => p.id === freshUserPlan) || null;
          }
        }
        console.log("[Subscriptions] userPlan resolved:", userPlanObj?.planTitle, "$" + userPlanObj?.price);

        // Check if admin upgraded the plan since last login
        const userId = userDetails?.id || "";
        const PLAN_KEY   = "mhb_last_seen_plan_" + userId;
        const BANNER_KEY = "mhb_upgrade_banner_" + userId;
        const lastSeenPlan   = localStorage.getItem(PLAN_KEY) || "";
        const freshTier      = userPlanObj?.planTier || "";
        const freshPrice     = parseInt(userPlanObj?.price || 0);
        const wasUpgraded    = freshTier && freshPrice > 0 && freshTier !== lastSeenPlan;
        // Show banner if: upgraded OR banner not yet dismissed for this plan
        const bannerStored   = localStorage.getItem(BANNER_KEY) || "";
        const showBanner     = freshTier && freshPrice > 0 && bannerStored !== freshTier;

        // Update last seen plan
        if (freshTier) localStorage.setItem(PLAN_KEY, freshTier);

        console.log("[Subscriptions] upgrade check — lastSeen:", lastSeenPlan, "fresh:", freshTier, "wasUpgraded:", wasUpgraded, "showBanner:", showBanner);

        this.setState({
          plansList: all, loading: false, userPlan: userPlanObj, userProfile,
          showUpgradeCelebration: wasUpgraded,
          celebrationPlan: wasUpgraded ? userPlanObj : null,
          showUpgradeBanner: showBanner,
          upgradeBannerPlan: showBanner ? userPlanObj : null,
          _bannerKey: BANNER_KEY,
        });
      } else {
        this.setState({ loading: false, userProfile });
      }
      if (this.props.setLoadingfalse) this.props.setLoadingfalse();
    } catch(e) {
      console.log("[Subscriptions] loadData error:", e.message);
      this.setState({ loading: false });
    }
  };

  isVendor = () => this.state.userProfile === "V";

  getCurrentTier = () => {
    const { userPlan } = this.state;
    if (!userPlan || !userPlan.price) return this.isVendor() ? "vendor_free" : "free";
    return this.isVendor()
      ? getVendorTierFromPrice(userPlan.price, userPlan.planTier)
      : getTierFromPrice(userPlan.price, userPlan.planTier);
  };

  getPlanByTier = (tier) => {
    const { plansList } = this.state;
    if (this.isVendor()) {
      // Try direct planTier match first, then price-based fallback
      return plansList.find(p => p.planTier === tier && p.userType === "V")
        || plansList.find(p => getVendorTierFromPrice(p.price, p.planTier) === tier && p.userType === "V")
        || null;
    }
    // Agent/lender: try direct planTier match first, then price-based fallback
    return plansList.find(p => p.planTier === tier && (p.userType === "AG" || p.userType === "L" || !p.userType))
      || plansList.find(p => getTierFromPrice(p.price, p.planTier) === tier && (p.userType === "AG" || p.userType === "L" || !p.userType))
      || null;
  };

  handleChoosePlan = (tierKey, stripeKey) => {
    const currentTier = this.getCurrentTier();
    if (tierKey === currentTier || tierKey === "vendor_free" || tierKey === "free") return;
    const link = stripeKey ? STRIPE_LINKS[stripeKey] : null;
    if (link) { window.open(link, "_blank"); return; }
    // Growth program handled by Stripe link above if available
    // Fallback modal
    const dbPlan = this.getPlanByTier(tierKey);
    const config = this.isVendor() ? VENDOR_TIER_CONFIG[tierKey] : AGENT_TIER_CONFIG[tierKey];
    this.setState({
      selectedPlan: { ...config, tier: tierKey, price: dbPlan?.price || config?.price || 0 },
      paymentVisible: true, cardholderName: "", cardNumber: "", cardMonth: "", cardYear: "", cardCode: "",
    });
  };

  handlePaymentSubmit = async () => {
    const { cardholderName, cardNumber, cardMonth, cardYear, cardCode, selectedPlan } = this.state;
    if (!cardholderName || !cardNumber || !cardMonth || !cardYear || !cardCode) {
      notification.error({ message: "Please fill in all payment fields" }); return;
    }
    this.setState({ submitting: true });
    try {
      await noAuthadd("sendMail", { entityData: { to: "support@myhomebuddha.com", from: this.props.context.state.userDetails?.email || "", subject: "Plan Upgrade — " + selectedPlan?.name, message: "Upgrade to " + selectedPlan?.name + " at $" + selectedPlan?.price + "/mo. Card ending: " + cardNumber.slice(-4), isRegistred: true } });
      notification.success({ message: "Upgrade request received!", description: "We will activate your plan within 24 hours.", duration: 6 });
      this.setState({ paymentVisible: false, submitting: false });
    } catch { notification.error({ message: "Failed — email support@myhomebuddha.com" }); this.setState({ submitting: false }); }
  };

  handleData = (e) => this.setState({ [e.target.name]: e.target.value });

  renderCell = (val, tier, dbPlan) => {
    if (val === true)  return <span style={{ color: GREEN, fontSize: "16px" }}>✓</span>;
    if (val === false) return <span style={{ color: BORDER, fontSize: "16px" }}>—</span>;
    if (typeof val === "string") {
      let v = val;
      if (dbPlan) v = v.replace("{z}", dbPlan.zipcodes || "?").replace("{s}", dbPlan.specialites || "?");
      return <span style={{ fontWeight: 600, color: "#1A202C", fontSize: "13px" }}>{v}</span>;
    }
    return <span style={{ color: MUTED }}>—</span>;
  };

  // ── AGENT/LENDER render ───────────────────────────────────────────────────
  renderAgentView() {
    const currentTier = this.getCurrentTier();
    const dbPlans = {};
    AGENT_TIERS.forEach(t => { dbPlans[t] = this.getPlanByTier(t) || { price: 0, zipcodes: 1, specialites: 1 }; });

    return (
      <div>
        {/* ── Upgrade Banner — shows when admin boosted the plan ──────────── */}
        {this.state.showUpgradeBanner && this.state.upgradeBannerPlan && (
          <div style={{ background: "linear-gradient(135deg, #276749, #2F855A)", borderRadius: "16px", padding: "16px 20px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "16px", boxShadow: "0 4px 20px rgba(39,103,73,0.25)", flexWrap: "wrap" }}>
            <div style={{ fontSize: "28px", flexShrink: 0 }}>🎉</div>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <div style={{ fontWeight: 800, color: "white", fontSize: "15px", marginBottom: "3px" }}>
                My Home Buddha upgraded your account!
              </div>
              <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "13px", lineHeight: 1.5 }}>
                You've been upgraded to <strong style={{ color: "white" }}>{this.state.upgradeBannerPlan.planTitle || this.state.upgradeBannerPlan.name}</strong> because you're a rockstar. Keep helping us grow and qualify for even more benefits!
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
              <a href="https://ai.myhomebuddha.com" target="_blank" rel="noopener noreferrer"
                style={{ background: "white", color: "#276749", borderRadius: "50px", padding: "8px 16px", fontSize: "12px", fontWeight: 800, textDecoration: "none", whiteSpace: "nowrap" }}>
                Use My Tools →
              </a>
              <button
                onClick={() => {
                  // Remember dismissed for this plan so it doesn't re-show
                  if (this.state._bannerKey && this.state.upgradeBannerPlan?.planTier) {
                    localStorage.setItem(this.state._bannerKey, this.state.upgradeBannerPlan.planTier);
                  }
                  this.setState({ showUpgradeBanner: false });
                }}
                style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "white", borderRadius: "50%", width: "28px", height: "28px", cursor: "pointer", fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Founding Banner */}
        <div style={{ background: "linear-gradient(135deg, #2A1600, #744210)", borderRadius: "16px", padding: "20px 24px", marginBottom: "24px", boxShadow: "0 4px 20px rgba(116,66,16,0.25)", display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: "220px" }}>
            <div style={{ color: GOLD, fontWeight: 800, fontSize: "11px", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: "4px" }}>🌟 Founding Partner Program</div>
            <div style={{ color: WHITE, fontWeight: 700, fontSize: "17px", marginBottom: "4px" }}>First 500 agents get prices locked forever</div>
            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", lineHeight: 1.5 }}>Join at Advanced or Professional and your founding rate is yours for life — plus a permanent badge and future equity & profit-share participation.</div>
          </div>
          <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: "12px", padding: "16px 20px", textAlign: "center", flexShrink: 0, border: "1px solid rgba(255,255,255,0.2)" }}>
            <div style={{ color: GOLD, fontWeight: 900, fontSize: "32px", lineHeight: 1 }}>{FOUNDING_SPOTS_LEFT}</div>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "11px", marginTop: "4px" }}>of {FOUNDING_SPOTS_TOTAL} spots left</div>
          </div>
        </div>

        {/* Current plan pill */}
        <div style={{ marginBottom: "20px", background: WHITE, borderRadius: "10px", padding: "12px 16px", border: "1px solid " + BORDER, display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ fontSize: "20px" }}>{AGENT_TIER_CONFIG[currentTier]?.emoji || "🌱"}</div>
          <div>
            <div style={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>Current Plan: {AGENT_TIER_CONFIG[currentTier]?.name || "Free"}</div>
            <div style={{ fontSize: "12px", color: MUTED }}>${this.state.userPlan?.price || 0}/month</div>
          </div>
          {(currentTier === "advanced" || currentTier === "professional" || currentTier === "team_leader") && (
            <div style={{ marginLeft: "auto", background: GOLD + "20", color: "#744210", borderRadius: "50px", padding: "3px 12px", fontSize: "11px", fontWeight: 800 }}>🌟 Founding Partner</div>
          )}
        </div>

        {/* 4 plan cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: "24px" }}>
          {AGENT_TIERS.map(tier => {
            const config = AGENT_TIER_CONFIG[tier];
            const dbPlan = dbPlans[tier];
            // Use DB price if valid AND matches expected tier range
            // Fall back to config founding price so display is never $0 or wrong tier
            const rawPrice = parseInt(dbPlan?.price) || 0;
            // Validate price is in right ballpark for tier
            const priceValid = (tier === "free" && rawPrice === 0)
              || (tier === "basic"        && rawPrice >= 1  && rawPrice < 15)
              || (tier === "advanced"     && rawPrice >= 15 && rawPrice < 60)
              || (tier === "professional" && rawPrice >= 60 && rawPrice < 199)
              || (tier === "team_leader"  && rawPrice >= 199);
            const price = priceValid ? rawPrice : (config.founding || 0);
            const isCurrent = tier === currentTier;
            const isPopular = config.isPopular;
            return (
              <div key={tier} style={{ background: WHITE, borderRadius: "14px", padding: "20px 16px", position: "relative", textAlign: "center", border: isCurrent ? "2px solid " + config.color : isPopular ? "2px solid " + NAVY : "1px solid " + BORDER, boxShadow: isPopular ? "0 4px 20px rgba(26,34,128,0.12)" : "none" }}>
                {isPopular && !isCurrent && <div style={{ position: "absolute", top: "-11px", left: "50%", transform: "translateX(-50%)", background: NAVY, color: WHITE, borderRadius: "50px", padding: "3px 14px", fontSize: "10px", fontWeight: 800, whiteSpace: "nowrap" }}>MOST POPULAR</div>}
                {config.isFounding && !isPopular && !isCurrent && <div style={{ position: "absolute", top: "-11px", left: "50%", transform: "translateX(-50%)", background: GOLD, color: "#2A1600", borderRadius: "50px", padding: "3px 14px", fontSize: "10px", fontWeight: 800, whiteSpace: "nowrap" }}>FOUNDING PRICE</div>}
                {isCurrent && <div style={{ position: "absolute", top: "-11px", left: "50%", transform: "translateX(-50%)", background: GREEN, color: WHITE, borderRadius: "50px", padding: "3px 14px", fontSize: "10px", fontWeight: 800 }}>✓ YOUR PLAN</div>}
                <div style={{ fontSize: "24px", marginBottom: "4px", marginTop: "6px" }}>{config.emoji}</div>
                <div style={{ fontWeight: 800, fontSize: "15px", color: config.color, marginBottom: "2px" }}>{config.name}</div>
                <div style={{ fontSize: "11px", color: MUTED, marginBottom: "12px" }}>{config.tagline}</div>
                <div style={{ marginBottom: "4px" }}>
                  <span style={{ fontWeight: 900, fontSize: "26px", color: NAVY }}>${price}</span>
                  <span style={{ fontSize: "12px", color: MUTED }}>/mo</span>
                </div>
                {config.isFounding && config.futurePrice > price && price > 0 && (
                  <div style={{ fontSize: "11px", color: GREEN, fontWeight: 700, marginBottom: "14px" }}>
                    <s style={{ color: "#E53E3E", fontWeight: 400 }}>${config.futurePrice}/mo</s>{" "}Save ${config.futurePrice - price}/mo
                  </div>
                )}
                <div style={{ marginTop: (config.isFounding && config.futurePrice > price && price > 0) ? "0" : "14px", display: "flex", flexDirection: "column", gap: "6px" }}>
                  {isCurrent ? (
                    <div style={{ padding: "9px", background: LIGHT, borderRadius: "50px", fontSize: "12px", color: MUTED, fontWeight: 600 }}>Current Plan</div>
                  ) : tier === "free" ? (
                    <div style={{ padding: "9px", background: LIGHT, borderRadius: "50px", fontSize: "12px", color: MUTED }}>Always Free</div>
                  ) : (
                    <button onClick={() => this.handleChoosePlan(tier, config.stripeKey)}
                      style={{ padding: "10px", background: isPopular ? NAVY : "transparent", color: isPopular ? WHITE : NAVY, border: "2px solid " + NAVY, borderRadius: "50px", cursor: "pointer", fontWeight: 700, fontSize: "12px" }}
                      onMouseEnter={e => { if (!isPopular) { e.target.style.background = NAVY; e.target.style.color = WHITE; }}}
                      onMouseLeave={e => { if (!isPopular) { e.target.style.background = "transparent"; e.target.style.color = NAVY; }}}>
                      {currentTier === "free" ? "Get " + config.name : "Switch to " + config.name}
                    </button>
                  )}
                  <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" style={{ fontSize: "11px", color: MUTED, textDecoration: "none", padding: "4px 0" }}>Learn more →</a>
                </div>
              </div>
            );
          })}
        </div>

        {/* Comparison table */}
        <div style={{ background: WHITE, borderRadius: "14px", border: "1px solid " + BORDER, overflow: "hidden", marginBottom: "24px" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid " + BORDER }}>
            <div style={{ fontWeight: 800, color: NAVY, fontSize: "16px" }}>Plan comparison</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", background: LIGHT, borderBottom: "1px solid " + BORDER }}>
            <div style={{ padding: "12px 16px" }} />
            {AGENT_TIERS.map(tier => {
              const config = AGENT_TIER_CONFIG[tier];
              const rawP = parseInt(dbPlans[tier]?.price) || 0;
              const price = rawP > 0 ? rawP : (tier === "free" ? 0 : AGENT_TIER_CONFIG[tier]?.founding || 0);
              return (
                <div key={tier} style={{ padding: "12px 8px", textAlign: "center", borderLeft: "1px solid " + BORDER, background: config.isPopular ? NAVY + "08" : "transparent" }}>
                  <div style={{ fontWeight: 800, fontSize: "13px", color: config.color }}>{config.name}</div>
                  <div style={{ fontSize: "11px", color: MUTED }}>${price} / month</div>
                  {config.isPopular && <div style={{ background: GOLD, color: "#2A1600", borderRadius: "50px", padding: "1px 8px", fontSize: "9px", fontWeight: 800, display: "inline-block", marginTop: "3px" }}>Most popular</div>}
                  {tier === currentTier && <div style={{ background: GREEN, color: WHITE, borderRadius: "50px", padding: "1px 8px", fontSize: "9px", fontWeight: 800, display: "inline-block", marginTop: "3px" }}>Your plan</div>}
                </div>
              );
            })}
          </div>
          {AGENT_COMPARISON_ROWS.map((row, i) => {
            if (row.section) return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", background: CREAM, borderBottom: "1px solid " + BORDER }}>
                <div style={{ padding: "8px 16px", gridColumn: "1 / -1", fontWeight: 800, fontSize: "11px", color: "#744210", textTransform: "uppercase", letterSpacing: ".06em" }}>{row.section}</div>
              </div>
            );
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", borderBottom: "1px solid " + BORDER + "80", background: i % 2 === 0 ? WHITE : LIGHT + "60" }}>
                <div style={{ padding: "11px 16px", fontSize: "13px", color: "#2D3748", display: "flex", alignItems: "center", gap: "6px" }}>
                  {row.label}
                  {row.tip && <span title={row.tip} style={{ color: MUTED, fontSize: "10px", cursor: "help", background: BORDER, borderRadius: "50%", width: "16px", height: "16px", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>i</span>}
                </div>
                {AGENT_TIERS.map(tier => (
                  <div key={tier} style={{ padding: "11px 8px", textAlign: "center", borderLeft: "1px solid " + BORDER, display: "flex", alignItems: "center", justifyContent: "center", background: AGENT_TIER_CONFIG[tier].isPopular ? NAVY + "04" : "transparent" }}>
                    {this.renderCell(row[tier], tier, dbPlans[tier])}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Team Leader section */}
        <div style={{ background: "linear-gradient(135deg, " + GREEN + "12, " + GREEN + "06)", border: "1px solid " + GREEN + "30", borderRadius: "16px", padding: "24px", marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "20px", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: "260px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <span style={{ fontSize: "28px" }}>🏆</span>
                <div>
                  <div style={{ fontWeight: 800, color: GREEN, fontSize: "16px" }}>Are You a Team Leader or Broker?</div>
                  <div style={{ fontSize: "12px", color: MUTED }}>Consider a Team Leader account</div>
                </div>
              </div>
              <div style={{ fontSize: "13px", color: "#2D3748", lineHeight: 1.7, marginBottom: "14px" }}>
                Get all Professional-level benefits for up to <strong>5 agents</strong> on your team — branded AI reports, buyer referrals, top-of-town placement, and revenue & equity participation.{" "}
                Have more team members?{" "}
                <a href={CALL_URL} target="_blank" rel="noopener noreferrer" style={{ color: GREEN, fontWeight: 700 }}>Click here to book a call</a>
                {" "}for larger package deals for your team or brokerage.
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {["✓ Everything in Professional","✓ Up to 5 team agent seats","✓ Team-wide branded reports","✓ Team leaderboard & analytics","✓ Dedicated success manager","✓ 🌟 Founding Partner badge"].map((f, i) => (
                  <div key={i} style={{ background: WHITE, border: "1px solid " + GREEN + "30", borderRadius: "8px", padding: "5px 10px", fontSize: "12px", color: "#276749", fontWeight: 600 }}>{f}</div>
                ))}
              </div>
            </div>
            <div style={{ background: WHITE, borderRadius: "14px", padding: "20px", minWidth: "200px", textAlign: "center", border: "2px solid " + GREEN, boxShadow: "0 4px 16px " + GREEN + "20", flexShrink: 0 }}>
              <div style={{ fontSize: "28px", marginBottom: "4px" }}>🏆</div>
              <div style={{ fontWeight: 800, fontSize: "15px", color: GREEN, marginBottom: "2px" }}>Team Leader</div>
              <div style={{ fontSize: "11px", color: MUTED, marginBottom: "12px" }}>Lead your market</div>
              <div style={{ marginBottom: "4px" }}>
                <span style={{ fontWeight: 900, fontSize: "28px", color: NAVY }}>${this.getPlanByTier("team_leader")?.price || 199}</span>
                <span style={{ fontSize: "12px", color: MUTED }}>/mo</span>
              </div>
              <div style={{ fontSize: "11px", color: GREEN, fontWeight: 700, marginBottom: "14px" }}>
                <s style={{ color: "#E53E3E", fontWeight: 400 }}>$299/mo</s> Save $100/mo
              </div>
              {currentTier === "team_leader" ? (
                <div style={{ padding: "9px", background: LIGHT, borderRadius: "50px", fontSize: "12px", color: MUTED, fontWeight: 600 }}>Current Plan</div>
              ) : (
                <button onClick={() => this.handleChoosePlan("team_leader", "agent_team_leader")}
                  style={{ width: "100%", padding: "10px", background: GREEN, color: WHITE, border: "none", borderRadius: "50px", cursor: "pointer", fontWeight: 700, fontSize: "12px" }}>
                  Get Team Leader
                </button>
              )}
              <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" style={{ display: "block", fontSize: "11px", color: MUTED, textDecoration: "none", marginTop: "8px" }}>Learn more →</a>
            </div>
          </div>
        </div>

        {/* Founding Partner details */}
        <div style={{ background: WHITE, borderRadius: "14px", padding: "20px 24px", border: "1px solid " + BORDER, marginBottom: "20px" }}>
          <div style={{ fontWeight: 800, color: NAVY, fontSize: "15px", marginBottom: "4px" }}>🌟 What is a Founding Partner?</div>
          <div style={{ fontSize: "13px", color: "#2D3748", lineHeight: 1.7, marginBottom: "12px" }}>
            The first 500 agents who join at Advanced, Professional, or Team Leader level become Founding Partners. Your discounted rate is locked forever — even as prices rise.
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {['🏷 "Founding Partner" badge on your profile','📈 First access to new AI features','💰 Future revenue-share & equity participation','🗳 Input on product roadmap decisions','🤝 Exclusive founding partner community'].map((item, i) => (
              <div key={i} style={{ background: GOLD + "15", borderRadius: "8px", padding: "8px 12px", fontSize: "12px", color: "#744210", fontWeight: 600 }}>{item}</div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── VENDOR render ─────────────────────────────────────────────────────────
  renderVendorView() {
    const currentTier = this.getCurrentTier();

    return (
      <div>
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <h2 style={{ fontWeight: 800, color: DARK, fontSize: "24px", margin: "0 0 8px" }}>Choose your plan</h2>
          <p style={{ color: MUTED, fontSize: "14px", lineHeight: 1.6, maxWidth: "680px" }}>
            Start free and upgrade as your business grows. Every plan puts you inside the Home Buddha ecosystem — the higher the tier, the more visibility, priority, and direct referrals you receive.
          </p>
        </div>

        {/* Current plan */}
        <div style={{ marginBottom: "20px", background: WHITE, borderRadius: "10px", padding: "12px 16px", border: "1px solid " + BORDER, display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ fontSize: "20px" }}>{VENDOR_TIER_CONFIG[currentTier]?.emoji || "🌱"}</div>
          <div>
            <div style={{ fontWeight: 700, color: DARK, fontSize: "14px" }}>Current Plan: {VENDOR_TIER_CONFIG[currentTier]?.name || "Free"}</div>
            <div style={{ fontSize: "12px", color: MUTED }}>${this.state.userPlan?.price || 0}/month</div>
          </div>
        </div>

        {/* 4 vendor plan cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "28px" }}>
          {VENDOR_TIERS.map(tier => {
            const config = VENDOR_TIER_CONFIG[tier];
            const dbPlan = this.getPlanByTier(tier);
            const price  = dbPlan ? parseInt(dbPlan.price) : config.price;
            const isCurrent = tier === currentTier;

            return (
              <div key={tier} style={{
                background: config.isDark ? DARK : WHITE,
                borderRadius: "14px", padding: "24px 20px", position: "relative",
                border: config.isPopular ? "2px solid " + GOLD : isCurrent ? "2px solid " + GREEN : "1px solid " + BORDER,
                boxShadow: config.isPopular ? "0 4px 24px rgba(212,146,26,0.2)" : "none",
                display: "flex", flexDirection: "column",
              }}>
                {config.isPopular && (
                  <div style={{ position: "absolute", top: "-11px", left: "50%", transform: "translateX(-50%)", background: GOLD, color: "#2A1600", borderRadius: "50px", padding: "3px 14px", fontSize: "10px", fontWeight: 800, whiteSpace: "nowrap" }}>Most popular</div>
                )}
                {isCurrent && !config.isPopular && (
                  <div style={{ position: "absolute", top: "-11px", left: "50%", transform: "translateX(-50%)", background: GREEN, color: WHITE, borderRadius: "50px", padding: "3px 14px", fontSize: "10px", fontWeight: 800 }}>✓ YOUR PLAN</div>
                )}

                <div style={{ fontSize: "11px", fontWeight: 800, color: config.isDark ? GOLD : config.color, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "6px" }}>{config.tagline}</div>
                <div style={{ fontWeight: 800, fontSize: "22px", color: config.isDark ? WHITE : DARK, marginBottom: "6px" }}>{config.name}</div>
                <div style={{ marginBottom: "4px" }}>
                  <span style={{ fontWeight: 900, fontSize: "32px", color: config.isDark ? WHITE : DARK }}>${price}</span>
                  <span style={{ fontSize: "13px", color: config.isDark ? "rgba(255,255,255,0.6)" : MUTED }}>
                    {tier === "vendor_growth" ? " / month + ad spend" : " / month"}
                  </span>
                </div>
                {config.isFounding && config.futurePrice && price && config.futurePrice > price && (
                  <div style={{ fontSize: "11px", color: GREEN, fontWeight: 700, marginBottom: "8px" }}>
                    <s style={{ color: "#E53E3E", fontWeight: 400 }}>${config.futurePrice}/mo</s> Save ${config.futurePrice - price}/mo
                  </div>
                )}

                {/* Short description */}
                {tier === "vendor_free" && <p style={{ fontSize: "13px", color: MUTED, lineHeight: 1.6, marginBottom: "16px" }}>1 zip code<br />Get listed on the Home Buddha Marketplace and start building your presence inside the network — no monthly cost.</p>}
                {tier === "vendor_basic" && <p style={{ fontSize: "13px", color: "#2D3748", lineHeight: 1.6, marginBottom: "16px" }}>Up to {dbPlan?.zipcodes || "10"} zip codes • Premier Vendor status<br />Enhanced visibility across multiple areas with a trusted Premier badge — and second-priority placement in direct recommendation referrals.</p>}
                {tier === "vendor_advanced" && <p style={{ fontSize: "13px", color: "#2D3748", lineHeight: 1.6, marginBottom: "16px" }}>Up to {dbPlan?.zipcodes || "20"} zip codes • Top-of-listing placement<br />Maximum local visibility, first-priority recommendation referrals, and your business featured directly inside Home Buddha Home Checkup Reports.</p>}
                {tier === "vendor_growth" && <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.75)", lineHeight: 1.6, marginBottom: "16px" }}>Everything in Advanced, plus managed lead generation<br />A fully managed lead generation system built for service providers who are serious about scaling. Includes ad creation, coaching, and a self-funding referral reward loop.</p>}

                <Divider style={{ margin: "0 0 16px", borderColor: config.isDark ? "rgba(255,255,255,0.15)" : BORDER }} />

                {/* Features */}
                <div style={{ flex: 1, marginBottom: "20px" }}>
                  {config.features.map((f, i) => {
                    const text   = typeof f === "string" ? f : f.text;
                    const muted  = typeof f === "object" && f.muted;
                    const parsed = text.replace("{z}", dbPlan?.zipcodes || "?");
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "10px" }}>
                        <span style={{ color: muted ? MUTED : (config.isDark ? GOLD : GREEN), fontSize: "14px", flexShrink: 0, marginTop: "1px" }}>{muted ? "✕" : "✓"}</span>
                        <span style={{ fontSize: "13px", color: muted ? MUTED : (config.isDark ? "rgba(255,255,255,0.85)" : "#2D3748"), lineHeight: 1.5, fontStyle: muted ? "italic" : "normal" }}>{parsed}</span>
                      </div>
                    );
                  })}
                </div>

                {/* CTA */}
                {isCurrent ? (
                  <div style={{ padding: "12px", background: config.isDark ? "rgba(255,255,255,0.1)" : LIGHT, borderRadius: "50px", fontSize: "13px", color: config.isDark ? "rgba(255,255,255,0.6)" : MUTED, fontWeight: 600, textAlign: "center" }}>Current Plan</div>
                ) : config.isApply ? (
                  <button onClick={() => window.open(CALL_URL, "_blank")}
                    style={{ width: "100%", padding: "13px", background: GOLD, color: "#2A1600", border: "none", borderRadius: "50px", cursor: "pointer", fontWeight: 800, fontSize: "14px" }}>
                    Apply for Growth Program
                  </button>
                ) : tier === "vendor_free" ? (
                  <div style={{ padding: "12px", border: "1px solid " + GOLD, borderRadius: "50px", fontSize: "13px", color: GOLD, fontWeight: 600, textAlign: "center", cursor: "default" }}>Create free account</div>
                ) : (
                  <button onClick={() => this.handleChoosePlan(tier, config.stripeKey)}
                    style={{ width: "100%", padding: "13px", background: config.isPopular ? GOLD : "transparent", color: config.isPopular ? "#2A1600" : GOLD, border: "2px solid " + GOLD, borderRadius: "50px", cursor: "pointer", fontWeight: 800, fontSize: "14px" }}>
                    {config.cta.replace("${p}", price)}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: "center", fontSize: "11px", color: MUTED, marginTop: "16px" }}>
          Questions? Email <a href="mailto:support@myhomebuddha.com" style={{ color: NAVY }}>support@myhomebuddha.com</a> · Cancel anytime · No contracts
        </div>
      </div>
    );
  }

  render() {
    const { loading, paymentVisible, selectedPlan, submitting } = this.state;
    if (loading) return <div style={{ textAlign: "center", padding: "60px" }}><Spin size="large" /></div>;

    return (
      <div style={{ background: LIGHT, padding: "24px 20px", minHeight: "500px" }}>
        {this.isVendor() ? this.renderVendorView() : this.renderAgentView()}

        <div style={{ textAlign: "center", fontSize: "11px", color: MUTED, marginTop: "8px" }}>
          <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" style={{ color: NAVY }}>View full plan details →</a>
          {" "}· Cancel anytime · No contracts
        </div>

        {/* ── Upgrade Celebration Modal ─────────────────────────────────────── */}
        {this.state.showUpgradeCelebration && this.state.celebrationPlan && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(10,12,30,0.85)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
            onClick={() => this.setState({ showUpgradeCelebration: false })}>
            <div style={{ background: "white", borderRadius: "20px", maxWidth: "480px", width: "100%", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
              onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div style={{ background: "linear-gradient(135deg,#1A2280,#2A3390)", padding: "28px", textAlign: "center" }}>
                <div style={{ fontSize: "40px", marginBottom: "8px" }}>🎉</div>
                <h2 style={{ color: "white", fontWeight: 900, fontSize: "22px", margin: "0 0 6px" }}>You've Been Upgraded!</h2>
                <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "13px", margin: 0 }}>The My Home Buddha team has a gift for you</p>
              </div>

              {/* Body */}
              <div style={{ padding: "24px" }}>
                <p style={{ fontSize: "14px", color: "#2D3748", lineHeight: 1.7, marginBottom: "16px" }}>
                  My Home Buddha has upgraded your membership because <strong>you're a rockstar</strong>. Keep up the effort helping us grow — and keep qualifying for even more upgraded benefits!
                </p>

                {/* Plan badge */}
                <div style={{ background: "linear-gradient(135deg,#1A2280,#2A3390)", borderRadius: "12px", padding: "14px 18px", marginBottom: "16px", textAlign: "center" }}>
                  <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>Your New Plan</div>
                  <div style={{ color: "white", fontSize: "20px", fontWeight: 900, marginTop: "4px" }}>
                    {this.state.celebrationPlan.planTitle || this.state.celebrationPlan.name}
                    {this.state.celebrationPlan.price ? ` — $${this.state.celebrationPlan.price}/mo` : ""}
                  </div>
                  {["advanced","professional","team_leader"].includes(this.state.celebrationPlan.planTier) && (
                    <div style={{ background: "#D4921A", color: "#2A1600", borderRadius: "50px", padding: "3px 14px", fontSize: "11px", fontWeight: 800, display: "inline-block", marginTop: "8px" }}>
                      🌟 Founding Partner — Price Locked Forever
                    </div>
                  )}
                </div>

                {/* CTA buttons */}
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={() => this.setState({ showUpgradeCelebration: false })}
                    style={{ flex: 1, padding: "11px", background: "#F7F9FC", color: "#718096", border: "1px solid #E2E8F0", borderRadius: "50px", cursor: "pointer", fontWeight: 600, fontSize: "13px" }}>
                    View My Plan
                  </button>
                  <a href="https://ai.myhomebuddha.com" target="_blank" rel="noopener noreferrer"
                    style={{ flex: 2, padding: "11px", background: "#1A2280", color: "white", border: "none", borderRadius: "50px", cursor: "pointer", fontWeight: 800, fontSize: "13px", textAlign: "center", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    Go Use My AI Tools →
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment modal fallback */}
        <Modal
          title={<div style={{ display: "flex", alignItems: "center", gap: "10px" }}><span style={{ fontSize: "20px" }}>💳</span><span style={{ color: NAVY, fontWeight: 800 }}>{selectedPlan ? "Upgrade to " + selectedPlan.name : "Payment"}{selectedPlan && <span style={{ fontWeight: 900, marginLeft: "6px" }}>${selectedPlan.price}/mo</span>}</span></div>}
          visible={paymentVisible} footer={null} onCancel={() => this.setState({ paymentVisible: false })}
          width={460} centered bodyStyle={{ padding: "28px 32px" }}>
          <div style={{ marginBottom: "14px" }}>
            <label style={{ fontSize: "12px", fontWeight: 600, color: MUTED, display: "block", marginBottom: "4px" }}>Cardholder Name</label>
            <Input name="cardholderName" placeholder="Full name on card" value={this.state.cardholderName} onChange={this.handleData} style={{ borderRadius: "8px", height: "40px" }} />
          </div>
          <div style={{ marginBottom: "14px" }}>
            <label style={{ fontSize: "12px", fontWeight: 600, color: MUTED, display: "block", marginBottom: "4px" }}>Card Number</label>
            <Input name="cardNumber" placeholder="1234 5678 9012 3456" value={this.state.cardNumber} onChange={this.handleData} maxLength={19} style={{ borderRadius: "8px", height: "40px" }} prefix={<img src={creditIcon} alt="" style={{ width: "20px", height: "20px" }} />} />
          </div>
          <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
            <div style={{ flex: 1 }}><label style={{ fontSize: "12px", fontWeight: 600, color: MUTED, display: "block", marginBottom: "4px" }}>Month</label><Input name="cardMonth" placeholder="MM" value={this.state.cardMonth} onChange={this.handleData} maxLength={2} style={{ borderRadius: "8px", height: "40px" }} /></div>
            <div style={{ flex: 1 }}><label style={{ fontSize: "12px", fontWeight: 600, color: MUTED, display: "block", marginBottom: "4px" }}>Year</label><Input name="cardYear" placeholder="YY" value={this.state.cardYear} onChange={this.handleData} maxLength={2} style={{ borderRadius: "8px", height: "40px" }} /></div>
            <div style={{ flex: 1 }}><label style={{ fontSize: "12px", fontWeight: 600, color: MUTED, display: "block", marginBottom: "4px" }}>CVV</label><Input name="cardCode" placeholder="123" value={this.state.cardCode} onChange={this.handleData} maxLength={4} style={{ borderRadius: "8px", height: "40px" }} /></div>
          </div>
          <Divider style={{ margin: "0 0 20px" }} />
          <div style={{ display: "flex", gap: "10px" }}>
            <button onClick={() => this.setState({ paymentVisible: false })} style={{ flex: 1, padding: "11px", background: "transparent", color: NAVY, border: "2px solid " + BORDER, borderRadius: "50px", cursor: "pointer", fontWeight: 600, fontSize: "13px" }}>Cancel</button>
            <button onClick={this.handlePaymentSubmit} disabled={submitting} style={{ flex: 2, padding: "11px", background: submitting ? "#ccc" : NAVY, color: WHITE, border: "none", borderRadius: "50px", cursor: submitting ? "default" : "pointer", fontWeight: 700, fontSize: "13px" }}>
              {submitting ? "Processing..." : "Subscribe — $" + (selectedPlan?.price || 0) + "/mo"}
            </button>
          </div>
          <div style={{ fontSize: "10px", color: MUTED, textAlign: "center", marginTop: "10px" }}>🔒 Secure · Cancel anytime · No setup fees</div>
        </Modal>
      </div>
    );
  }
}

export default withAppContext(withRouter(Subscriptions));
